<template>
  <component
    :is="isElement"
    :href="url"
    v-bind="mergedProps"
    :class="['base-action', themeClass, sizeClass]"
    @click="$emit('click', $event)"
    @mouseover="$emit('mouseover', $event)"
    @mouseleave="$emit('mouseleave', $event)">
    <slot name="iconPrepend"></slot>

    <span v-if="!hideText && !!$slots.default">
      <slot></slot>
    </span>

    <slot name="iconAppend"></slot>
  </component>
</template>

<script>
import _ from 'lodash'

const themes = ['primary', 'secondary', 'outlined', 'outlined-white', 'flat', 'skeleton', 'destructive', 'destructive-flat', 'link']
const sizes = ['large', 'medium']

export default {
  props: {
    url: {
      type: String,
      required: false,
      default: null
    },
    theme: {
      type: String,
      default: 'primary',
      validator: (val) => _.includes(themes, val)
    },
    size: {
      type: String,
      default: 'medium',
      validator: (val) => _.includes(sizes, val)
    },
    onlyIconMobile: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    isElement() {
      return this.url ? 'a' : 'button'
    },
    themeClass() { return `base-action--${this.theme}` },
    sizeClass() { return `base-action--${this.size}` },
    mergedProps() {
      return this.isElement === 'button' ? { type: 'button', ...this.$attrs } : this.$attrs
    },
    hideText() {
      return this.mq('to-small') && this.onlyIconMobile
    }
  }
}
</script>

<style lang="scss">

// GENERAL
.base-action {
  @include reset-button;
  justify-content: center;
  position: relative;
  text-decoration: none;
  border-radius: 2px;
}

.base-action span {
  line-height: 1;
}

.base-action:focus {
  outline: 2px solid black;
}

.base-action:disabled {
  pointer-events: none;
  cursor: default;
}

// SIZES

.base-action--medium {
  @include body-smaller-semi-bold;
  padding: 6px 16px;
  height: 32px
}

.base-action--large {
  @include body-semi-bold;
  padding: 14px 24px;
  height: 40px
}

// ONLY ICON
.base-action--medium:has(.base-icon:only-child)  {
  padding: 6px;
}

.base-action--large:has(.base-icon:only-child)  {
  padding: 14px;
}

// THEME PRIMARY

.base-action--primary {
  color: color(white);
  background-color: color(brand-blue-3);
}

.base-action--primary:hover {
  background-color: color(brand-blue-2);
}

.base-action--primary:disabled {
  color: color(gray-2);
  background-color: color(gray-4);
}

// THEME SECONDARY

.base-action--secondary {
  color: color(white);
  background-color: color(button-action-green);
}

.base-action--secondary:hover {
  background-color: color(button-action-border-green);
}

.base-action--secondary:disabled {
  color: color(gray-2);
  background-color: color(gray-4);
}

// THEME OUTLINED

.base-action--outlined {
  color: color(brand-blue-3);
  border: 1px solid color(brand-blue-3);
  background-color: transparent;
}

.base-action--outlined:hover {
  background-color: color(blue-4);
}

.base-action--outlined:disabled {
  color: color(gray-2);
  border: 1px solid color(gray-4);
}

// THEME OUTLINED WHITE

.base-action--outlined-white {
  color: color(brand-blue-3);
  border: 1px solid color(brand-blue-3);
  background-color: color(white);
}

.base-action--outlined-white:hover {
  background-color: color(blue-4);
}

.base-action--outlined-white:disabled {
  color: color(gray-2);
  border: 1px solid color(gray-4);
}

// THEME FLAT

.base-action--flat {
  color: color(brand-blue-3);
  background-color: transparent;
}

.base-action--flat:hover {
  background-color: color(blue-4);
}

.base-action--flat:disabled {
  color: color(gray-2);
}

// THEME SKELETON

.base-action--skeleton {
  color: color(black);
  background-color: transparent;
}

.base-action--skeleton:hover {
  background-color: color(gray-4);
}

.base-action--skeleton:disabled {
  color: color(gray-2);
}

// THEME DESTRUCTIVE

.base-action--destructive {
  color: color(red-4);
  border: 1px solid color(red-4);
  background-color: transparent;
}

.base-action--destructive:hover {
  background-color: color(red-2);
}

.base-action--destructive:disabled {
  color: color(gray-2);
  border: 1px solid color(gray-4);
}

// THEME DESTRUCTIVE FLAT

.base-action--destructive-flat {
  color: color(red-4);
  background-color: transparent;
}

.base-action--destructive-flat:hover {
  background-color: color(red-2);
}

.base-action--destructive-flat:disabled {
  color: color(gray-2);
}

// THEME LINK

.base-action--link {
  height: auto;
  padding: 0;
  border: 1px solid transparent;
  background-color: transparent;
}
</style>
